import { useState, useCallback } from "react";
import { useCreate, useUpdate, useNotify, useRefresh, useGetList } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { Star, Send, Loader2, CheckCircle2, ExternalLink } from "lucide-react";

interface Review {
  id: string;
  status: string;
  rating: number | null;
  review_text: string;
  review_source: string;
  submitted_at: string | null;
}

interface ReviewRequestCardProps {
  jobId: string;
  contactId: string | number;
  contactName: string;
}

export function ReviewRequestCard({ jobId, contactId, contactName }: ReviewRequestCardProps) {
  const [create] = useCreate();
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [sending, setSending] = useState(false);

  const { data: reviews } = useGetList<Review>("reviews", {
    pagination: { page: 1, perPage: 5 },
    sort: { field: "created_at", order: "DESC" },
    filter: { job_id: jobId },
  });

  const existingReview = reviews?.[0];
  const hasReview = existingReview && existingReview.status !== "pending";

  const handleRequestReview = useCallback(async () => {
    setSending(true);
    try {
      if (existingReview) {
        await update("reviews", {
          id: existingReview.id,
          data: {
            status: "requested",
            requested_at: new Date().toISOString(),
            request_method: "sms",
          },
        }, { returnPromise: true });
      } else {
        await create("reviews", {
          data: {
            job_id: jobId,
            contact_id: contactId,
            status: "requested",
            requested_at: new Date().toISOString(),
            request_method: "sms",
          },
        }, { returnPromise: true });
      }

      await create("events", {
        data: {
          contact_id: contactId,
          job_id: jobId,
          event_type: "review_request_sent",
          payload: { contact_name: contactName, method: "sms" },
        },
      }, { returnPromise: true });

      // Update contact
      await update("contacts", {
        id: contactId,
        data: { review_requested_at: new Date().toISOString() },
      }, { returnPromise: true });

      notify("Review request sent", { type: "success" });
      refresh();
    } catch (err) {
      notify(`Failed: ${(err as Error).message}`, { type: "error" });
    } finally {
      setSending(false);
    }
  }, [jobId, contactId, contactName, existingReview, create, update, notify, refresh]);

  return (
    <Card>
      <CardContent className="p-4">
        <h4 className="text-xs font-semibold text-muted-foreground mb-3 flex items-center gap-1.5">
          <Star className="w-3.5 h-3.5" />
          REVIEW REQUEST
        </h4>

        {existingReview?.status === "submitted" ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span className="text-sm font-medium text-green-700">Review submitted</span>
            </div>
            {existingReview.rating && (
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-4 h-4" style={{
                    color: i < existingReview.rating! ? "#d97706" : "#d1d5db",
                    fill: i < existingReview.rating! ? "#d97706" : "none",
                  }} />
                ))}
              </div>
            )}
            {existingReview.review_text && (
              <p className="text-sm text-muted-foreground italic">
                "{existingReview.review_text.substring(0, 100)}"
              </p>
            )}
          </div>
        ) : existingReview?.status === "requested" ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Review request sent to {contactName}. Awaiting response.
            </p>
            <button onClick={handleRequestReview} disabled={sending}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100">
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Resend Request
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Send {contactName} a review request via SMS.
            </p>
            <button onClick={handleRequestReview} disabled={sending}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Send Review Request
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
